///
/// EarthBrowser - Shapefile Handler
///
/// Handles Shapefile (.shp, .zip, folder) drag & drop
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { registerDatasetSourceFile } from '@/components/features/user-datasets/datasetSourceFiles'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { MemoryDataProvider } from '@/data-providers/MemoryDataProvider'
import type { DataRow } from '@/data-providers/types'
import { logger } from '@/utils/LogDispatcher'
import { getOrCreateDatasetWindow } from '@/utils/windowUtils'
import type { ShapefileParseDataChunk, ShapefileParseProgress, ShapefileWorkerType } from '@/workers/shapefile/index'
import ShapefileWorker from '@/workers/shapefile/index.ts?worker'
import { proxy, wrap, type Remote } from 'comlink'
import { BaseFileHandler } from './BaseFileHandler'

// The DOM lib in use doesn't declare FileSystemDirectoryHandle.values(),
// so cast through a minimal structural type for directory iteration
interface DirectoryHandleValues {
	values(): AsyncIterable<FileSystemFileHandle | FileSystemDirectoryHandle>
}

export class ShapefileFileHandler extends BaseFileHandler {
	private workerRef: Remote<ShapefileWorkerType> | null = null
	private worker: Worker | null = null

	canHandle(file: File): boolean {
		const fileName = file.name.toLowerCase()
		return fileName.endsWith('.shp') || fileName.endsWith('.zip')
	}

	// No-op - worker is created on demand
	initialize(): void {}

	/**
	 * Ensures the worker is created, creating it on-demand if needed
	 */
	private ensureWorker(): void {
		if (!this.worker) {
			this.worker = new ShapefileWorker()
			this.workerRef = wrap<ShapefileWorkerType>(this.worker)
		}
	}

	cleanup(): void {
		if (this.worker) {
			this.worker.terminate()
			this.worker = null
			this.workerRef = null
		}
	}

	async handleFile(file: File): Promise<string | null> {
		// Create worker on-demand
		this.ensureWorker()

		if (!this.workerRef) {
			console.error('Shapefile worker could not be created')
			return null
		}

		// Get or create window for this file
		const windowId = getOrCreateDatasetWindow(
			this.context.getState,
			this.context.dispatch,
			file.name,
			'shapefile',
			{ width: 700, height: 500 }
		)

		// If window already exists, do nothing
		if (!windowId) {
			return null
		}

		try {
			// Accumulate data chunks as they arrive
			const allData: Record<string, unknown>[] = []

			// Create MemoryDataProvider with empty data initially
			const provider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: [],
			})

			// Register the provider immediately - this makes it available to the window
			DataProviderRegistry.register(windowId, provider)

			// Parse shapefile in worker with progress reporting and chunked data transfer
			const parseResult = await this.workerRef.parseFile(
				file,
				proxy((progress: ShapefileParseProgress) => {
					// Dispatch progress event for the window to listen to
					window.dispatchEvent(new CustomEvent(`dataset:progress:${windowId}`, {
						detail: {
							windowId,
							filename: file.name,
							processedRows: progress.featuresParsed,
							percentage: progress.percentage
						}
					}))
				}),
				proxy((dataChunk: ShapefileParseDataChunk) => {
					// Accumulate data as it arrives from worker
					allData.push(...dataChunk.chunk)
				})
			)

			// Update provider with all the parsed data
			const finalProvider = new MemoryDataProvider({
				id: windowId,
				name: file.name,
				data: allData as DataRow[],
			})

			// Re-register with the complete data
			DataProviderRegistry.register(windowId, finalProvider)

			// Keep the GeoJSON conversion (geometry + DBF attributes) around
			// for cloud upload, before the complete event so the browser
			// toolbar sees it on first render
			if (parseResult.geojson) {
				registerDatasetSourceFile(windowId, {
					file: parseResult.geojson,
					format: 'geojson',
					featureCount: parseResult.validCount
				})
			}

			// Dispatch completion event for the window to listen to
			window.dispatchEvent(new CustomEvent(`dataset:complete:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					rowCount: allData.length
				}
			}))

			console.log(`Shapefile loaded: ${file.name} (${allData.length} features)`)
			return windowId
		} catch (error) {
			console.error('Failed to load shapefile:', error)

			// Dispatch error event
			window.dispatchEvent(new CustomEvent(`dataset:error:${windowId}`, {
				detail: {
					windowId,
					filename: file.name,
					error: error instanceof Error ? error.message : String(error)
				}
			}))

			throw error
		}
	}

	/**
	 * Helper to recursively find shapefile files in a directory
	 */
	async findShapefiles(dirHandle: FileSystemDirectoryHandle): Promise<Array<{
		shp: File
		dbf?: File
		prj?: File
		name: string
	}>> {
		const shapefiles: Array<{
			shp: File
			dbf?: File
			prj?: File
			name: string
		}> = []
		const fileMap = new Map<string, {
			shp?: File
			dbf?: File
			prj?: File
		}>()

		// Recursively walk directory
		const walkDirectory = async (handle: FileSystemDirectoryHandle, path: string = '') => {
			const entries = (handle as unknown as DirectoryHandleValues).values()
			for await (const entry of entries) {
				const fullPath = path ? `${path}/${entry.name}` : entry.name

				if (entry.kind === 'directory') {
					// Recurse into subdirectories
					await walkDirectory(entry, fullPath)
				} else if (entry.kind === 'file') {
					const file = await entry.getFile()
					const fileName = entry.name.toLowerCase()

					// Extract base name (case-insensitive for matching)
					const lastDotIndex = entry.name.lastIndexOf('.')
					if (lastDotIndex === -1) continue // Skip files without extension

					const baseName = entry.name.substring(0, lastDotIndex).toLowerCase()
					const key = path ? `${path}/${baseName}` : baseName

					if (fileName.endsWith('.shp')) {
						// Found a shapefile
						const existing = fileMap.get(key) || {}
						existing.shp = file
						fileMap.set(key, existing)
					} else if (fileName.endsWith('.dbf')) {
						// Found a DBF file
						const existing = fileMap.get(key) || {}
						existing.dbf = file
						fileMap.set(key, existing)
					} else if (fileName.endsWith('.prj')) {
						// Found a PRJ projection file
						const existing = fileMap.get(key) || {}
						existing.prj = file
						fileMap.set(key, existing)
					}
				}
			}
		}

		await walkDirectory(dirHandle)

		// Collect all shapefiles (with or without companion files)
		for (const [key, files] of fileMap.entries()) {
			if (files.shp) {
				shapefiles.push({
					shp: files.shp,
					dbf: files.dbf,
					prj: files.prj,
					name: key.split('/').pop() || files.shp.name
				})
			}
		}

		return shapefiles
	}

	/**
	 * Handle a directory containing shapefiles
	 */
	async handleDirectory(dirHandle: FileSystemDirectoryHandle): Promise<void> {
		// Create worker on-demand
		this.ensureWorker()

		if (!this.workerRef) {
			console.error('Shapefile worker could not be created')
			return
		}

		const shapefiles = await this.findShapefiles(dirHandle)

		if (shapefiles.length === 0) {
			logger.warn(`No shapefiles found in directory: ${dirHandle.name}`)
			return
		}

		// Process each shapefile found
		for (const { shp, dbf, prj, name } of shapefiles) {
			try {
				// Get or create window for this file
				const windowId = getOrCreateDatasetWindow(
					this.context.getState,
					this.context.dispatch,
					name,
					'shapefile',
					{ width: 700, height: 500 }
				)

				// If window already exists, skip this file
				if (!windowId) {
					continue
				}

				// Accumulate data chunks as they arrive
				const allData: Record<string, unknown>[] = []

				// Log which companion files are present
				const companionFiles = []
				if (dbf) companionFiles.push('DBF')
				if (prj) companionFiles.push('PRJ')
				if (companionFiles.length > 0) {
					console.log(`Loading shapefile with companion files: ${companionFiles.join(', ')}`)
				}

				// Create MemoryDataProvider with empty data initially
				const provider = new MemoryDataProvider({
					id: windowId,
					name: name,
					data: [],
				})

				// Register the provider immediately - this makes it available to the window
				DataProviderRegistry.register(windowId, provider)

				// Parse shapefile in worker (pass all available files)
				const parseResult = await this.workerRef.parseFile(
					shp,
					proxy((progress: ShapefileParseProgress) => {
						// Dispatch progress event for the window to listen to
						window.dispatchEvent(new CustomEvent(`dataset:progress:${windowId}`, {
							detail: {
								windowId,
								filename: name,
								processedRows: progress.featuresParsed,
								percentage: progress.percentage
							}
						}))
					}),
					proxy((dataChunk: ShapefileParseDataChunk) => {
						// Accumulate data as it arrives from worker
						allData.push(...dataChunk.chunk)
					}),
					dbf, // Pass the DBF file if it exists
					prj  // Pass the PRJ file if it exists
				)

				// Update provider with all the parsed data
				const finalProvider = new MemoryDataProvider({
					id: windowId,
					name: name,
					data: allData as DataRow[],
				})

				// Re-register with the complete data
				DataProviderRegistry.register(windowId, finalProvider)

				// Keep the GeoJSON conversion (geometry + DBF attributes)
				// around for cloud upload, before the complete event so the
				// browser toolbar sees it on first render
				if (parseResult.geojson) {
					registerDatasetSourceFile(windowId, {
						file: parseResult.geojson,
						format: 'geojson',
						featureCount: parseResult.validCount
					})
				}

				// Dispatch completion event for the window to listen to
				window.dispatchEvent(new CustomEvent(`dataset:complete:${windowId}`, {
					detail: {
						windowId,
						filename: name,
						rowCount: allData.length
					}
				}))

				console.log(`Shapefile loaded from directory: ${name} (${allData.length} features)`)

				// Only process the first shapefile for now
				break
			} catch (error) {
				console.error(`Failed to load shapefile ${name}:`, error)
			}
		}
	}
}

